import { useState, useEffect } from 'react';
import { categoriesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { FolderTree, Plus, Edit2, Trash2, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Categories = () => {
    const { isAdmin } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const { data } = await categoriesAPI.getAll();
            setItems(data);
        } catch {
            toast.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const openCreate = () => {
        setEditingId(null);
        setName('');
        setOpen(true);
    };

    const openEdit = (c) => {
        setEditingId(c.id);
        setName(c.name);
        setOpen(true);
    };

    const handleSave = async () => {
        if (!name.trim()) {
            toast.error('Category name is required');
            return;
        }
        setSaving(true);
        try {
            if (editingId) {
                await categoriesAPI.update(editingId, { name });
                toast.success('Category updated');
            } else {
                await categoriesAPI.create({ name });
                toast.success('Category created');
            }
            setOpen(false);
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (c) => {
        if (!window.confirm(`Delete category "${c.name}"?`)) return;
        try {
            await categoriesAPI.delete(c.id);
            toast.success('Category deleted');
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Delete failed');
        }
    };

    return (
        <div data-testid="categories-page" className="space-y-8 animate-fadeIn">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-[11px] uppercase tracking-[2px] text-[#C08478] font-semibold mb-2">
                        Master Data
                    </p>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">Categories</h1>
                    <p className="text-gray-500 mt-1">
                        Group products by type (Skincare, Haircare, Makeup&hellip;) for filtering and reporting.
                    </p>
                </div>
                <Button
                    onClick={openCreate}
                    data-testid="new-category-btn"
                    className="bg-[#1A1A1A] hover:bg-[#C08478] text-white"
                >
                    <Plus size={16} className="mr-2" /> Add Category
                </Button>
            </div>

            <Card className="border-0 shadow-sm">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FolderTree className="text-[#C08478]" size={20} />
                        All Categories ({items.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="py-16 flex justify-center">
                            <Loader2 className="animate-spin text-[#C08478]" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="py-16 text-center text-gray-400">
                            No categories yet. Click <span className="font-medium">Add Category</span>.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-[#F5F5F5]">
                                    <TableHead>Category</TableHead>
                                    <TableHead className="text-right">Products</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map((c) => (
                                    <TableRow key={c.id} data-testid={`category-row-${c.id}`}>
                                        <TableCell className="font-medium">{c.name}</TableCell>
                                        <TableCell className="text-right">
                                            <Badge className="bg-[#264653]/20 text-[#264653] font-mono text-[11px]">
                                                {c.product_count}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => openEdit(c)}
                                                data-testid={`edit-category-${c.id}`}
                                                className="text-gray-500 hover:text-[#C08478]"
                                            >
                                                <Edit2 size={16} />
                                            </Button>
                                            {isAdmin() && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDelete(c)}
                                                    data-testid={`delete-category-${c.id}`}
                                                    className="text-gray-500 hover:text-red-500"
                                                >
                                                    <Trash2 size={16} />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid="category-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingId ? 'Edit Category' : 'New Category'}</DialogTitle>
                        <DialogDescription>
                            Renaming updates the name on all linked products automatically.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Name *</Label>
                            <Input
                                data-testid="category-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Skincare, Haircare, Makeup..."
                                autoFocus
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            data-testid="save-category"
                            className="bg-[#1A1A1A] hover:bg-[#C08478] text-white"
                        >
                            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
                            {editingId ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Categories;
